import Styles from './ProjectsView.module.scss';
import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import ProductGridCard from 'components/global/ProductCards/ProductGridCard/ProductGridCard';
import ProductListCard from 'components/global/ProductCards/ProductListCard/ProductListCard';
import PurchaseGridCard from 'components/global/PurchaseCards/PurchaseGridCard/PurchaseGridCard';
import PurchaseListCard from 'components/global/PurchaseCards/PurchaseListCard/PurchaseListCard';
import SearchBar from 'components/global/SearchBar/SearchBar';
import { useDebounce, usePurchases, Message, AuthContext, ViewContext } from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import { PaymentPending } from 'views/account/paymentPending';
import { Notification } from 'components/notifications/notification';
import FilterBar from 'components/FilterBar/FilterBar';

import {
    Tabs, Tab, Box, Pagination, ToggleButton, ToggleButtonGroup,
    Typography, Dialog, Button, Divider
} from '@mui/material';
import { GridView, ViewList, SearchOff, PlaylistAddCheck, AccountBalance, School } from '@mui/icons-material';
import { ToastContainer } from 'react-toastify';

export function ProjectsView(props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const context = useContext(AuthContext);
    const viewContext = useContext(ViewContext);
    const [currentTab, setCurrentTab] = useState(searchParams.get('tab') || 'my-projects');

    const [viewType, setViewType] = useState('grid');
    const [searchValue, setSearchValue] = useState('');
    const [searchActive, setSearchActive] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showAssociation, setShowAssociation] = useState(false);
    const [associationId, setAssociationId] = useState();

    const [projectCerts, setProjectCerts] = useState([])

    const debouncedSearchValue = useDebounce(searchValue, 500, 3);

    const [appliedTypes, setAppliedTypes] = useState([]);
    const [appliedTags, setAppliedTags] = useState([]);
    const [tagsOptions, setTagsOptions] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [pageSize, setPageSize] = useState(12);

    const hasCalculated = useRef(false);
    const currentCols = useRef(0);
    const gridRef = useRef(null);

    const fetchTags = async () => {
        if (tagsOptions.length > 0) return;

        try {
            setTagsLoading(true);
            const response = await axios.get('/api/tags');
            setTagsOptions(response?.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch tags', error.message);
        } finally {
            setTagsLoading(false);
        }
    };

    // Reset hasInitialized when tab or viewType changes
    useEffect(() => {
        hasCalculated.current = false;
        currentCols.current = 0;
    }, [viewType, currentTab]);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            if (viewType === 'grid' && pageSize === 0) return;
            setLoading(true);
            try {
                let url;
                switch (currentTab) {
                    case 'my-projects':
                        url = '/api/user/labs';
                        break;
                    case 'purchase':
                        url = '/api/labs/available';
                        break;
                    case 'org':
                        url = '/api/labs/org';
                        break;
                    default:
                        url = '/api/user/labs';
                }

                const response = await axios.get(url, {
                    params: {
                        page: pageNumber,
                        pageSize: viewType === 'grid' ? pageSize : 10,
                        search: debouncedSearchValue.trim(),
                        isProject: 1,
                        ...(appliedTypes.length > 0 && { type: appliedTypes[0] }),
                        ...(appliedTags.length > 0 && { tags: appliedTags.join(',') })
                    }
                });

                if (cancelled) return;

                const data = response?.data?.data;
                setProjects(data);

                const totalCount = response?.data?.total || response?.data?.totalCount ||
                    (Array.isArray(data) ? data.length : 0);
                setTotalPages(Math.ceil(totalCount / pageSize));
            } catch (err) {
                if (!cancelled) viewContext.notification.show('Error fetching data', 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        return () => { cancelled = true };
    }, [
        currentTab,
        pageNumber,
        debouncedSearchValue,
        appliedTypes,
        appliedTags,
        pageSize,
        viewType
    ]);

    useEffect(() => {
        const calculateColumns = () => {
            const container = gridRef.current;

            if (!container) return;
            const containerWidth = container.clientWidth;
            // get ONE card (even from small data)
            const card = container.querySelector(':scope > *');
            if (!card) return;

            const cardWidth = card.getBoundingClientRect().width;

            if (!cardWidth) return;

            const cols = Math.max(1, Math.floor(containerWidth / cardWidth));

            const newPageSize = cols * 4;

            if (newPageSize === pageSize) return;

            currentCols.current = cols;
            setPageSize(newPageSize);
        };

        const timer = setTimeout(calculateColumns, 100);

        window.addEventListener('resize', calculateColumns);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', calculateColumns);
        };
    }, [viewType, projects.length]);

    useEffect(() => {
        setPageNumber(1)
    }, [pageSize])

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setSearchParams({ tab: newValue });
        setPageNumber(1);
        setSearchValue('');
        setSearchActive(false);
    };

    const handlePageChange = (event, value) => {
        setPageNumber(value);
    };

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setViewType(nextView);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        let cleanedText = pastedText
            .replace(/^\s+/, "")
            .replace(/\s{2,}/g, " ")
            .trim();

        if (cleanedText) {
            setSearchValue(cleanedText);
            setPageNumber(1);
        }
    };

    const handleProductAssociation = (association) => {
        setShowAssociation(true);
        setAssociationId(association);
    };

    const handleUserLabClick = async (project) => {
        const projectId = project.id;

        // Check if the project is part of an internship
        if (project.is_internship === 1) {
            setProjectCerts([])
            try {
                const { data } = await axios.get('/api/labs/internships/all', { params: { labId: projectId } });
                setProjectCerts(data.data ?? []);
            } catch (err) {
                viewContext.notification.show(
                    'Failed to fetch the internships',
                    'error',
                    true
                );
            }
            return;
        }

        // 1. Disabled case → redirect immediately
        if (project.isDisabled) {
            navigate(`/labs/info/${projectId}`);
            return;
        }

        // 2. Certification-based lab → fetch cert list for tooltip
        if (project.cert_id && project.is_internship === 0) {
            setProjectCerts([])
            try {
                const { data } = await axios.post('/api/labs/lpath/all', { labId: projectId });
                setProjectCerts(data.data ?? []);
            } catch (err) {
                viewContext.notification.show(
                    'Failed to fetch the certificates',
                    'error',
                    true
                );
            }
            return;
        }

        // 3. Normal lab handling
        const assignUrl = `/api/user/labs/${projectId}/assign/0`;
        if (project.status === "not_started") {
            try {
                await axios.post(assignUrl);
                navigate(`/labs/${projectId}/nodes`);
            } catch (err) {
                console.error("Error:", err?.response?.status);
                navigate('/oops');
            }
            return;
        }

        // 4. Lab already started → direct navigation
        navigate(`/labs/${projectId}/nodes`);
    }

    const handleCertTooltipClick = (itemId, isInternship = null) => {
        if (isInternship === 1) {
            navigate(`/internships/${itemId}/info`);
            return;
        } else {
            navigate(`/lpaths/${itemId}/learningpath`);
            return;
        }
    };

    const handleCardClick = (project) => {
        switch (currentTab) {
            case 'my-projects':
                handleUserLabClick(project);
                break
            case 'purchase':
                navigate(`/labs/info/${project.id}`)
                break
            case 'org':
                navigate(`/labs/info/${project.id}`)
                break
            default:
                handleUserLabClick(project);
        }
    };

    const renderLabCards = () => {
        const isMyProjects = currentTab === 'my-projects';
        const mode = currentTab === 'org' ? 'enroll' :
            (context?.user?.plan === 'free' || context?.user?.plan === 'hybrid') ? 'purchase' : 'enroll';
        return projects.map((project) => {
            const isInternship = project?.is_internship === 1;
            if (isMyProjects) {
                return viewType === 'grid' ? (
                    <ProductGridCard
                        data={project}
                        key={project.id}
                        onClick={handleCardClick}
                        button_text={project.buttonText}
                        showProgressBar={true}
                        showInfoChips={true}
                        showMedalChips={true}
                        showBadge={true}
                        isPartOfOtherProducts={projectCerts}
                        onCertTooltipClick={handleCertTooltipClick}
                        tooltipMessage={props.t('Labs.UserLabs.tooltip')}
                        isInternship={isInternship}
                    />
                ) : (
                    <ProductListCard
                        data={project}
                        key={project.id}
                        onClick={handleCardClick}
                        button_text={project.buttonText}
                        showProgressBar={true}
                        showInfoChips={true}
                        showMedalChips={true}
                        showBadge={true}
                        isPartOfOtherProducts={projectCerts}
                        onCertTooltipClick={handleCertTooltipClick}
                        tooltipMessage={props.t('Labs.UserLabs.tooltip')}
                        isInternship={isInternship}
                    />
                );
            } else {
                return viewType === 'grid' ? (
                    <PurchaseGridCard
                        key={project.id}
                        data={project}
                        onCardClick={handleCardClick}
                        handleProductAssociation={handleProductAssociation}
                        mode={mode}
                        type="course"
                        showBadge={true}
                    />
                ) : (
                    <PurchaseListCard
                        key={project.id}
                        data={project}
                        onCardClick={handleCardClick}
                        handleProductAssociation={handleProductAssociation}
                        mode={mode}
                        type="course"
                        showBadge={true}
                    />
                );
            }
        });
    }

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Notification call='lab' />
            {context?.user?.subscription === 'active' ? (
                context?.user?.onboarded ? (
                    <Message
                        closable
                        title={props.t('CapstoneProject.UserProject.info.title')}
                        text={props.t('CapstoneProject.UserProject.info.text')}
                        type="info"
                    />
                ) : (
                    <Message
                        title={'Profile Setup Pending'}
                        text={'Please complete your profile setup to unlock all features and get started.'}
                        type="warning"
                        buttonText={'Setup Profile'}
                        buttonLink={'/account'}
                    />
                )
            ) : (
                <PaymentPending />
            )}
            <div className={Styles.cardContainer}>
                <div className={Styles.tabContainer}>
                    <BreadCrumbs
                        onClickPaths={['']}
                        crumbs={['Projects']}
                    />
                </div>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        aria-label="labs tabs"
                    >
                        <Tab label="My Projects" value="my-projects" />
                        <Tab label="Available Projects" value="purchase" />
                        {context?.user?.plan === 'hybrid' && (
                            <Tab label="Organization Projects" value="org" />
                        )}
                    </Tabs>
                </Box>
                {/* TOP CONTROLS ROW */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "1em",
                        mb: 2
                    }}
                >
                    <SearchBar
                        value={searchValue}
                        setValue={(val) => {
                            setSearchValue(val);
                            setPageNumber(1);
                            setSearchActive(val.length > 0);
                        }}
                        placeholder={props.t('Labs.LabsPurchase.search.placeholder')}
                        searchActive={searchActive}
                        onClear={() => {
                            setSearchValue('');
                            setPageNumber(1);
                            setSearchActive(false);
                        }}
                        onPaste={handlePaste}
                    />

                    <ToggleButtonGroup
                        value={viewType}
                        exclusive
                        onChange={handleViewChange}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ViewList fontSize="medium" />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridView fontSize="medium" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {/* FILTER ROW */}
                <FilterBar
                    typeOptions={[{ label: "Custom Projects", value: "custom_resource" }]}
                    tagOptions={tagsOptions.map(t => ({
                        label: t.tag_name,
                        value: t.tag_name.toLowerCase()
                    }))}
                    appliedTypes={appliedTypes}
                    setAppliedTypes={setAppliedTypes}
                    appliedTags={appliedTags}
                    setAppliedTags={setAppliedTags}
                    quickTags={["AI", "Full Stack", "React", "JavaScript"]}
                    onFetchTags={fetchTags}
                />

                {projects?.length > 0 && currentTab !== 'my-projects' && (
                    <Typography variant="h7" sx={{ display: 'flex', gap: '16px', padding: '24px 0px', mb: '-15px' }}>
                        {currentTab === 'org' ?
                            props.t('CapstoneProject.ProjectPurchase.enroll') :
                            (context?.user?.plan === 'free' || context?.user?.plan === 'hybrid') ?
                                props.t('CapstoneProject.ProjectPurchase.purchase') :
                                props.t('CapstoneProject.ProjectPurchase.enroll')
                        }
                    </Typography>
                )}

                {/* Cards */}
                {loading ? (
                    <LoadingBackdrop isOpen={true} />
                ) : projects.length > 0 ? (
                    <div style={{ width: "100%" }}>
                        <div
                            ref={viewType === 'grid' ? gridRef : null}
                            className={viewType === 'grid' ? Styles.gridWrapper : Styles.listWrapper}
                        >
                            {renderLabCards()}
                        </div>
                    </div>
                ) : (searchActive ? (
                    <div className={Styles.emptyState}>
                        <SearchOff sx={{ fontSize: 56 }} className={Styles.icon} />
                        <div className={Styles.enrollMessage}>{props.t('CapstoneProject.ProjectPurchase.resultMessage')}</div>
                    </div>
                ) : (
                    <div className={`${Styles.emptyState} ${Styles.emptyStateBlue}`}>
                        {currentTab === 'my-projects' ? (
                            <School sx={{ fontSize: 56 }} className={Styles.icon} />
                        ) : currentTab === 'org' ? (
                            <AccountBalance sx={{ fontSize: 56 }} className={Styles.icon} />
                        ) : (
                            <PlaylistAddCheck sx={{ fontSize: 56 }} className={Styles.icon} />
                        )}
                        <div className={Styles.enrollMessage}>
                            {currentTab === 'my-projects' ?
                                "Your enrolled projects will show up here. Click on available projects to get started."
                                : currentTab === 'org' ?
                                    'Your organization projects will show up here. Currently there are no organizational projects'
                                    : "You are currently enrolled in all available projects on the platform. Please check back for new projects soon"
                            }
                        </div>
                    </div>
                ))}

                {/* Pagination */}
                {!loading && projects.length > 0 && (
                    <div className={Styles.pageBtnContainer}>
                        <Pagination
                            count={totalPages}
                            color="primary"
                            variant='outlined'
                            page={pageNumber}
                            onChange={handlePageChange}
                            showFirstButton
                            showLastButton
                        />
                    </div>
                )}

                {/* Association Dialog */}
                <Dialog open={showAssociation} onClose={() => setShowAssociation(false)}>
                    <div style={{ width: "fit-content", height: 'fit-content', padding: '12px', backgroundColor: '#DBEAFE' }}>
                        <Message
                            title={props.t("CapstoneProject.ProjectPurchase.info.title")}
                            type='info'
                            text={props.t("CapstoneProject.ProjectPurchase.info.text")}
                            redirect={associationId ? `/lpaths/info/${associationId}` : `/lpaths`}
                            redirectText={props.t("CapstoneProject.ProjectPurchase.info.link")}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-37px' }}>
                            <Button onClick={() => setShowAssociation(false)}>
                                {props.t("CapstoneProject.ProjectPurchase.info.button")}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </div >
        </>
    );
}